// 글로리샵 분석 대시보드. gloryShopLog 컬렉션(전용 로그, 필터 없이 orderBy timestamp desc
// 페이징 — 복합 인덱스 회피)을 페치·집계해 KPI/일별/상품별/유저별 표를 그린다.
//
// env/verse 는 이 페이지 로컬 셀렉트로 바꾸지 않는다. fetchGloryLogs·adminQueryCount 는
// AdminRPCClient(사이드바에서 고른 활성 verse 연결)를 그대로 타므로 실제로 바꿀 수 있는
// 값이 아니다 — ShopRevenuePage(별도 VX API 직접 페치)와 달리 verse 를 여기서 선택하게
// 두면 눌러도 반영되지 않는 죽은 UI가 된다. 대신 활성 verse 를 읽기 전용으로 표기하고
// FunnelPanel/AttendanceDistributionPanel 등 다른 admin.queryCount 기반 화면과 같은
// 패턴을 따른다.

import { useEffect, useMemo, useState } from 'react';
import { gatewayUrlOf, resolveVerseEntry } from '@rb/shared/admin-verses';
import { aggregateGloryLogs } from '@rb/shared/analytics/glory-agg';
import type { GloryShopLogDoc } from '@rb/shared/analytics/glory-log.types';
import {
  GloryDailyCard,
  GloryItemCard,
  GloryKpiRow,
  GloryTopSpendersCard,
} from '@/components/admin/GloryShopTables';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { adminQueryCount } from '@/client/admin.client';
import { fetchGloryLogs } from '@/client/glory-log.client';
import { downloadCsv } from '@/lib/download';
import { nicknameKey } from '@/shop/game-nickname';
import { isWiderPeriod, PERIOD_OPTIONS, type Period, periodStartMs } from '@/shop/revenue-period';
import { useGameNicknames } from '@/shop/use-game-nicknames';
import { useVerseOwnership } from '@/shop/use-verse-ownership';
import { useEffectiveGatewayId } from '@/verse/active-verse.store';

type Phase = 'idle' | 'loading' | 'done' | 'error';

const ALL_ITEMS = 'all';

export default function GloryShopAnalyticsPage() {
  const { account, activeVerseId } = useVerseOwnership();
  const gatewayId = useEffectiveGatewayId();
  const verse = activeVerseId ? resolveVerseEntry(activeVerseId) : null;

  const [period, setPeriod] = useState<Period>('7d');
  const [itemFilter, setItemFilter] = useState<string>(ALL_ITEMS);
  const [phase, setPhase] = useState<Phase>('idle');
  const [loaded, setLoaded] = useState(0);
  const [error, setError] = useState('');
  const [logs, setLogs] = useState<GloryShopLogDoc[]>([]);
  /** maxPages 상한으로 조회가 중간에 끊긴 채 끝났는지 — true 면 화면 집계가 과소집계다. */
  const [truncated, setTruncated] = useState(false);
  /** 마지막으로 성공 조회한 시점의 기간/verse — 이후 기간을 넓히거나 verse 를 바꾸면 재조회가 필요하다. */
  const [loadedPeriod, setLoadedPeriod] = useState<Period | null>(null);
  const [loadedVerseId, setLoadedVerseId] = useState<string | null>(null);

  // 로그 도입 이전 구간 참고용 — 재화 원장 누적 건수. 실패해도 본 화면은 그대로 동작한다.
  const [lifetime, setLifetime] = useState<{ purchase: number; gacha: number } | null>(null);

  // Sidebar 의 VerseSelector 는 이 페이지를 언마운트하지 않고 activeVerseId 만 바꾸므로,
  // deps 에 activeVerseId 를 넣지 않으면 옛 verse 의 숫자가 화면에 그대로 남는다. verse 가
  // 바뀌는 즉시 lifetime 을 비워 옛 숫자가 잠깐이라도 새 verse 것처럼 보이지 않게 한다.
  // biome-ignore lint/correctness/useExhaustiveDependencies: activeVerseId 는 재조회 트리거 전용 의존성(effect 본문에서 직접 참조하지 않음)
  useEffect(() => {
    let alive = true;
    setLifetime(null);
    Promise.all([
      adminQueryCount({
        collection: 'currencyTransactions',
        filters: [{ field: 'reason', operator: '==', value: 'glory_shop_purchase' }],
      }),
      adminQueryCount({
        collection: 'currencyTransactions',
        filters: [{ field: 'reason', operator: '==', value: 'glory_gacha_draw' }],
      }),
    ])
      .then(([purchase, gacha]) => {
        if (alive) setLifetime({ purchase, gacha });
      })
      .catch(() => {
        if (alive) setLifetime(null);
      });
    return () => {
      alive = false;
    };
  }, [activeVerseId]);

  async function handleLoad() {
    setPhase('loading');
    setError('');
    setLoaded(0);
    const fetchedPeriod = period;
    const fetchedVerseId = activeVerseId;
    try {
      const fromMs = periodStartMs(period);
      const { logs: rows, truncated: didTruncate } = await fetchGloryLogs({
        fromMs,
        onProgress: (n) => setLoaded(n),
      });
      setLogs(rows);
      setTruncated(didTruncate);
      // 방금 받은 데이터에 더는 없는 상품으로 필터가 걸려 있으면 표가 설명 없이 비어버린다 —
      // 자동으로 '전체' 로 되돌린다(필터한 상품이 여전히 있으면 유지).
      if (itemFilter !== ALL_ITEMS && !rows.some((l) => l.itemId === itemFilter)) {
        setItemFilter(ALL_ITEMS);
      }
      setLoadedPeriod(fetchedPeriod);
      setLoadedVerseId(fetchedVerseId);
      setPhase('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPhase('error');
    }
  }

  // 페치는 불러온 시점의 기간/verse 를 기준으로 멈추므로, 그 뒤 기간을 넓히거나 verse 를
  // 바꾸면 화면은 여전히 옛 조회 결과를 보여준다 — 재조회가 필요함을 경고한다.
  const periodWidened = loadedPeriod != null && isWiderPeriod(period, loadedPeriod);
  const verseChanged = loadedVerseId != null && loadedVerseId !== activeVerseId;
  const needsReload = periodWidened || verseChanged;

  const filtered = useMemo(
    () => (itemFilter === ALL_ITEMS ? logs : logs.filter((l) => l.itemId === itemFilter)),
    [logs, itemFilter]
  );
  const agg = useMemo(() => aggregateGloryLogs(filtered), [filtered]);
  const itemOptions = useMemo(() => [...new Set(logs.map((l) => l.itemId))].sort(), [logs]);

  const nicknameTargets = useMemo(
    () =>
      activeVerseId
        ? agg.byAccount.slice(0, 50).map((r) => ({ verseId: activeVerseId, wallet: r.account }))
        : [],
    [agg.byAccount, activeVerseId]
  );

  const gameNicknames = useGameNicknames({
    targets: nicknameTargets,
    account,
    gatewayUrl: gatewayUrlOf(gatewayId),
  });

  const handleExportCsv = () => {
    // 화면 집계(aggregateGloryLogs)는 refunded 행을 소비에서 제외하지만 CSV 는 원본
    // 행을 그대로 내보낸다 — refunded 컬럼이 없으면 운영자가 CSV 의 cost 를 그대로
    // 합산해 화면의 "총 소모 글로리" 와 다른 값을 얻고도 원인을 알 수 없다.
    const rows = filtered.map((l) => ({
      account: l.account,
      kind: l.kind,
      itemId: l.itemId,
      cost: l.cost,
      pullCount: l.pullCount ?? '',
      refunded: l.refunded ? 'true' : '',
      timestamp: l.timestamp,
    }));
    const columns = [
      { key: 'account', header: 'account' },
      { key: 'kind', header: 'kind' },
      { key: 'itemId', header: 'itemId' },
      { key: 'cost', header: 'cost' },
      { key: 'pullCount', header: 'pullCount' },
      { key: 'refunded', header: 'refunded' },
      { key: 'timestamp', header: 'timestamp' },
    ];
    const today = new Date().toISOString().slice(0, 10);
    downloadCsv(`glory-shop-${period}-${today}.csv`, rows, columns);
  };

  const loading = phase === 'loading';

  return (
    <section className="flex flex-col gap-4">
      <header>
        <h2 className="text-2xl font-semibold tracking-tight">글로리샵 분석</h2>
        <p className="text-sm text-muted-foreground">
          전용 로그(gloryShopLog) 기준 글로리 구매/뽑기 집계 대시보드. 필터 없이 최신순 페이징하며
          기간 시작 이전 행을 만나면 페치를 멈춥니다 — '전체'만 전량 페치라 느립니다. 현재 조회 대상
          verse: <span className="font-mono">{verse?.label ?? '연결 안 됨'}</span> (전환은
          사이드바의 Verse 셀렉터를 이용하세요).
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">조회 설정</CardTitle>
          <CardDescription>
            활성 verse 의 gloryShopLog 를 기간·상품으로 필터링합니다.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">기간</span>
            <Select value={period} onValueChange={(v) => setPeriod(v as Period)}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">상품</span>
            <Select value={itemFilter} onValueChange={setItemFilter}>
              <SelectTrigger className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_ITEMS}>전체</SelectItem>
                {itemOptions.map((id) => (
                  <SelectItem key={id} value={id}>
                    {id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button type="button" onClick={handleLoad} disabled={loading}>
            {loading ? '불러오는 중…' : '로그 불러오기'}
          </Button>

          {phase === 'done' && (
            <Button type="button" variant="outline" onClick={handleExportCsv}>
              CSV 다운로드 ({filtered.length.toLocaleString()}건)
            </Button>
          )}

          {loading && (
            <span className="text-sm text-muted-foreground">
              {loaded.toLocaleString()}건 로드됨
            </span>
          )}
        </CardContent>
      </Card>

      <Alert>
        <AlertTitle>로그 도입 이전 구간</AlertTitle>
        <AlertDescription>
          이 페이지는 전용 로그(gloryShopLog)가 배포된 시점 이후의 구매만 집계합니다. 그 이전 누적
          건수는 재화 원장 카운트로만 확인할 수 있습니다.
          {lifetime && (
            <>
              {' '}
              재화 원장 기준 전체 누적: 확정 구매 {lifetime.purchase.toLocaleString()}건 · 뽑기{' '}
              {lifetime.gacha.toLocaleString()}건.
            </>
          )}
        </AlertDescription>
      </Alert>

      {error && (
        <Alert variant="destructive">
          <AlertDescription className="whitespace-pre-wrap break-words">{error}</AlertDescription>
        </Alert>
      )}

      {phase === 'done' && truncated && (
        <Alert variant="destructive">
          <AlertTitle>일부만 불러왔습니다</AlertTitle>
          <AlertDescription>
            페이지 상한에 도달해 조회 기간을 전부 채우지 못했습니다. 아래 집계는 과소집계일 수
            있습니다 — 기간을 좁혀 다시 불러오세요.
          </AlertDescription>
        </Alert>
      )}

      {needsReload && !loading && (
        <Alert>
          <AlertTitle>다시 불러와야 합니다</AlertTitle>
          <AlertDescription>
            {verseChanged && (
              <p>활성 verse 가 바뀌었습니다. 아래 데이터는 이전 verse 기준입니다.</p>
            )}
            {periodWidened && (
              <p>
                현재 표는 직전에 불러온 기간(
                {PERIOD_OPTIONS.find((o) => o.value === loadedPeriod)?.label}) 데이터라
                과소집계입니다.
              </p>
            )}
            <p>"로그 불러오기" 를 다시 눌러 주세요.</p>
          </AlertDescription>
        </Alert>
      )}

      {phase === 'done' && (
        <>
          <GloryKpiRow agg={agg} />
          <GloryDailyCard agg={agg} />
          <GloryItemCard agg={agg} />
          <GloryTopSpendersCard
            agg={agg}
            nicknameOf={(acc) =>
              activeVerseId
                ? (gameNicknames.nicknames.get(nicknameKey(activeVerseId, acc)) ?? undefined)
                : undefined
            }
          />
        </>
      )}
    </section>
  );
}
